import * as Collapsible from '@radix-ui/react-collapsible'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'

import { Button } from '@/components/button/button'
import SvgIcon from '@/components/svg-icon/svgIcon'
import { Typography } from '@/components/typography/typography'
import { fontStyles } from '@/components/typography/typographyUtils'
import { cn } from '@/libs/tw'
import { formatCurrency } from '@/utils/formatCurrency'
import { translate } from '@/utils/translator'

import { useRoomContext } from '../../hooks/useRoomContext'
import { useServices } from '../../hooks/useSerives'
import { Rate as TRate } from '../../types'
import { RecommendedFlag } from '../recommendedFlag'
import { RoomProps } from './room'
import { Services } from './services'

type RoomRateProps = RoomProps & {
	rate: TRate
}

export const Rate = ({
	room,
	rate,
	selectedRate,
	selectedServices,
	onSelect,
	onSelectRate,
	onSelectService,
}: RoomRateProps) => {
	const { roomConfig, setIsEditMode, roomNumber, showTouristTax } = useRoomContext()
	const [open, setOpen] = useState(false)

	const uniqRateCode = room.roomTypeCode + '-' + rate.ratePlanCode
	const isSelected = selectedRate === uniqRateCode
	const isRecommended = rate.ratePlanCode === roomConfig.roomSuggestion?.ratePlanCode

	const touristTax = rate.price.taxes?.find((tax) => !tax.inclusive)?.description

	useEffect(() => {
		const isRoomAndRateRecommended =
			rate.ratePlanCode === roomConfig.roomSuggestion?.ratePlanCode &&
			room.roomTypeCode === roomConfig.roomSuggestion?.roomTypeCode

		if (isRoomAndRateRecommended) {
			setOpen(true)
			onSelectRate(uniqRateCode)
		}
	}, [
		onSelectRate,
		rate.ratePlanCode,
		room.roomTypeCode,
		roomConfig.roomSuggestion,
		uniqRateCode,
	])

	const { data: services } = useServices({
		adults: roomConfig.adults,
		children: roomConfig.children.length,
		ratePlanCode: rate.ratePlanCode,
		roomTypeCode: room.roomTypeCode,
		enabled: isSelected,
	})

	const handleRateSelection = () => {
		setOpen(true)
		onSelectRate(uniqRateCode)
	}

	const handleRoomSelection = () => {
		setIsEditMode(false)
		onSelect(
			room,
			rate,
			services
				?.filter((s) => selectedServices.find((ss) => ss.id === s.serviceCode))
				.map((s) => ({
					...s,
					quantity: selectedServices.find((ss) => ss.id === s.serviceCode)?.quantity ?? 1,
				}))
		)
	}

	useEffect(() => {
		setOpen(isSelected)
	}, [isSelected])

	const checkBoxId = `${roomNumber}-${rate.ratePlanCode}-checkbox`

	return (
		<Collapsible.Root
			open={open}
			onOpenChange={setOpen}
			className={cn(
				'border-b border-textColor border-opacity-30 py-16 last:border-none md:border-dark-strong md:border-opacity-30'
			)}
		>
			<div className={cn('flex justify-between gap-16')}>
				<div className={cn('flex justify-start gap-16')}>
					<label
						htmlFor={checkBoxId}
						className={cn(
							'relative inline-block h-32 w-32 cursor-pointer',
							isRecommended && 'h-auto'
						)}
					>
						<input
							onKeyDown={(e) => e.key === 'Enter' && handleRateSelection()}
							id={checkBoxId}
							type="checkbox"
							checked={isSelected}
							onChange={handleRateSelection}
							className={cn(
								'relative z-10 h-32 w-32 cursor-pointer appearance-none rounded-[100%] border border-textColor bg-primary md:border-roomSelection-accentColor md:bg-naturel'
							)}
						/>
						{isSelected && (
							<div
								className={cn(
									'absolute left-4 top-4 z-10 h-[24px] w-[24px] transform rounded-[100%] bg-textColor md:bg-roomSelection-accentColor'
								)}
							/>
						)}
						{isRecommended && (
							<div className="-mt-12 ml-4 w-24">
								<RecommendedFlag />
							</div>
						)}
					</label>
					<div
						className={cn('flex cursor-pointer flex-col gap-16')}
						onClick={handleRateSelection}
					>
						<Typography style={'p-m'} element="h4">
							{rate.title}
						</Typography>
						{rate.cancellationDeadline ? (
							<Typography style={'p'} element="p" className="hidden md:block">
								{translate('room-selection-cancel-till')}{' '}
								{format(new Date(rate.cancellationDeadline), 'dd.MM.yyy / HH:mm')}
							</Typography>
						) : (
							<Typography style={'p'} element="p" className="hidden md:block">
								{translate('room-selection-no-cancel-till')}
							</Typography>
						)}
						{showTouristTax && touristTax && (
							<Typography style={'p-s'} element="p" className="">
								{touristTax}
							</Typography>
						)}
					</div>
				</div>
				<div className={cn('flex gap-16 md:gap-48')}>
					<Typography style="p-m-bold" element="p">
						{formatCurrency(rate.price.amountAfterTax, true)}
					</Typography>
					<Collapsible.Trigger asChild className={cn('group')}>
						<button className={cn('flex gap-12')}>
							<Typography element="span" style="p" className="hidden md:block">
								{translate('room-selection-details')}
							</Typography>
							<SvgIcon
								collectionName="ibe3"
								iconName="chevron"
								className="h-24 w-24 transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] group-data-[state=closed]:rotate-180"
							/>
						</button>
					</Collapsible.Trigger>
				</div>
			</div>
			<Collapsible.Content className="overflow-hidden data-[state=closed]:animate-slideUpC data-[state=open]:animate-slideDownC">
				<div className={cn('mt-12 flex flex-col gap-12  pl-48')}>
					{rate.cancellationDeadline ? (
						<Typography style={'p'} element="p" className="md:hidden">
							{translate('room-selection-cancel-till')}{' '}
							{format(new Date(rate.cancellationDeadline), 'dd.MM.yyy / HH:mm')}
						</Typography>
					) : (
						<Typography style={'p'} element="p" className="md:hidden">
							{translate('room-selection-no-cancel-till')}
						</Typography>
					)}
					<div
						dangerouslySetInnerHTML={{ __html: rate.description }}
						className={cn(fontStyles['p'], 'rateDescription pb-12')}
					/>
					<div>
						{isSelected && services && services.length > 0 && (
							<Services
								services={services}
								selectedServices={selectedServices}
								onSelect={onSelectService}
							/>
						)}
					</div>
				</div>

				{isSelected && (
					<Button
						style={'primary'}
						onClick={handleRoomSelection}
						className={cn(
							'mt-32 w-full bg-roomSelection-button text-roomSelection-buttonText md:bg-roomSelection-buttonDesktop md:text-roomSelection-buttonTextDesktop'
						)}
					>
						<Typography element="span" style="p-ms-bold">
							{translate('room-selection-room-booking')}
						</Typography>
					</Button>
				)}
			</Collapsible.Content>
		</Collapsible.Root>
	)
}
